import { Core } from "@faire/design-tokens";
import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { throttleIfPossible } from "@faire/web--source/common/globals/throttleIfPossible";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { createToast, dismissToast } from "@faire/web--source/slate/Toast";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import singleRetailerBoard from "@faire/web-api--source/routes/boards/retailerBoardToken";
import React from "react";
import styled from "styled-components";

import { ImageGrid } from "@faire/retailer/components/ImageGrid";
import { useFetchSingleBoard } from "@faire/retailer/features/RetailerBoards/hooks/boardsHooks";

interface IProps {
  boardToken: string | undefined;
  productImage: IImage | undefined;
  onChangeBoardClick: (() => void) | undefined;
  boardItemIsMoving?: boolean;
}

export const SaveToBoardToast: React.FC<IProps> = (props) => {
  const { boardToken, productImage, onChangeBoardClick, boardItemIsMoving } =
    props;
  const { board } = useFetchSingleBoard(boardToken);

  return (
    <Row>
      <StyledImageGrid
        images={board?.cover_images ?? [productImage]}
        imageSize={64}
      />
      <Spacer width="2x" />
      <ContentWrapper>
        <Typography color={Core.text.inverse}>
          {boardItemIsMoving ? (
            <StrictLocalMsg
              defaultMessage="Moved to"
              description={{
                text: "Title displaying which board a product was moved to",
                img: "https://cdn.faire.com/fastly/a4c0d779bde198d112146320031701d06a79beddb1d2d950e743f3f731919488.png",
              }}
            />
          ) : (
            <StrictLocalMsg
              defaultMessage="Saved to"
              description={{
                text: "Title displaying which board a product was saved to",
                img: "https://cdn.faire.com/fastly/296f1b8f4a2974392fb723b3729892fa2f60a1a34ef84ec84059ff7e072ae4b7.png",
              }}
            />
          )}
        </Typography>
        <Button
          variant="tertiary"
          linkTo={singleRetailerBoard(boardToken ?? "")}
          style={{ width: "fit-content", textDecoration: "none" }}
          tertiaryColor={Core.text.inverse}
        >
          <Typography
            color={Core.text.inverse}
            variant="paragraphSansMedium"
            truncate
          >
            {board?.name}
          </Typography>
        </Button>
        {onChangeBoardClick ? (
          <>
            <Spacer height="1x" />
            <Button
              variant="tertiary"
              tertiaryColor={Core.text.inverse}
              style={{ alignSelf: "flex-start" }}
              onClick={() => onChangeBoardClick?.()}
            >
              <StrictLocalMsg
                defaultMessage="Choose different board"
                description={{
                  text: "Link to change which board an item is saved to",
                  img: "https://cdn.faire.com/fastly/751244b314731e08440dde80a783968bd3c0306b933e1752bdb2201a3e06cee3.png",
                }}
              />
            </Button>
          </>
        ) : null}
      </ContentWrapper>
    </Row>
  );
};

const ContentWrapper = styled(Column)`
  min-width: 150px;
`;

const StyledImageGrid = styled(ImageGrid)`
  width: 98px;
`;

let activeToastId: string = "";

export const showSaveToBoardToast = (props: IProps) => {
  closeSaveToBoardToastIfActive();
  createToast(<SaveToBoardToast {...props} />, {
    timeout: "long",
    onOpen: (toastId: string) => {
      activeToastId = toastId;
      getWindow()?.addEventListener("scroll", handleScrollWhenToastIsActive, {
        passive: true,
      });
    },
  });
};

export const closeSaveToBoardToastIfActive = () => {
  if (activeToastId) {
    dismissToast(activeToastId);
    activeToastId = "";
  }
};

const handleScrollWhenToastIsActive = throttleIfPossible(() => {
  closeSaveToBoardToastIfActive();
  getWindow()?.removeEventListener("scroll", handleScrollWhenToastIsActive);
}, 50);
